import React, { useState, useEffect, useMemo } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiFilter, FiDownload, FiTrendingUp, FiTrendingDown, FiCalendar, FiDollarSign, FiTag, FiRepeat } from 'react-icons/fi';
import './IncomeExpenses.css';
import axios from 'axios';
import { END_POINT, getJwtToken } from '../../Redux/AdminReducer/action';
import ModalPortal, { MODAL_Z } from '../../Components/CommonComponents/ModalPortal';
import exportToExcel from '../../UtilityHookFunctions/exportToExcel';
import fetchAllPages, { formatExportDate } from '../../UtilityHookFunctions/fetchAllPages';


export const incomeExpenseaxios = {
  getRecords: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    return axios.get(`${END_POINT}/income-expense?${queryParams.toString()}`, {headers: {Authorization: getJwtToken()}});
  },

  getRecord: (id) => axios.get(`${END_POINT}/income-expense/${id}`, {headers: {Authorization: getJwtToken()}}),

  createRecord: (recordData) => axios.post(`${END_POINT}/income-expense`, recordData, {headers: {Authorization: getJwtToken()}}),

  updateRecord: (id, recordData) => axios.put(`${END_POINT}/income-expense/${id}`, recordData, {headers: {Authorization: getJwtToken()}}),

  deleteRecord: (id) => axios.delete(`${END_POINT}/income-expense/${id}`, {headers: {Authorization: getJwtToken()}}),

  getSummary: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    return axios.get(`${END_POINT}/income-expense/summary/stats?${queryParams.toString()}`, {headers: {Authorization: getJwtToken()}});
  },

  getRecurringRecords: () => axios.get(`${END_POINT}/income-expense/recurring/list`, {headers: {Authorization: getJwtToken()}}),

  getRecordsByTag: (tag) => axios.get(`${END_POINT}/income-expense/tags/${tag}`, {headers: {Authorization: getJwtToken()}}),

  getCategories: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    return axios.get(`${END_POINT}/income-expense/categories?${queryParams.toString()}`, {headers: {Authorization: getJwtToken()}});
  },

  createCategory: (categoryData) => axios.post(`${END_POINT}/income-expense/categories`, categoryData, {headers: {Authorization: getJwtToken()}}),

  deleteCategory: (id) => axios.delete(`${END_POINT}/income-expense/categories/${id}`, {headers: {Authorization: getJwtToken()}}),
};

const IncomeExpenses = () => {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [isCustomCategory, setIsCustomCategory] = useState(false);
  const [usedCategories, setUsedCategories] = useState({ income: [], expense: [] });
  const [customCategories, setCustomCategories] = useState({ income: [], expense: [] });

  // Manage Categories modal
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryManagerType, setCategoryManagerType] = useState('expense');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [categorySaving, setCategorySaving] = useState(false);

  // Paid From connectivity states
  const [paidFrom, setPaidFrom] = useState('none'); // none | cash | card | bank
  const [cashLink, setCashLink] = useState({
    type: 'physical-cash',
    location: '',
    walletProvider: '',
    date: new Date().toISOString().slice(0, 10),
    currency: 'INR',
  });
  const [cards, setCards] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [cardLink, setCardLink] = useState({ cardId: '', date: new Date().toISOString().slice(0, 10), currency: 'INR' });
  const [bankLink, setBankLink] = useState({ accountId: '', date: new Date().toISOString().slice(0, 10), currency: 'INR' });
  const [filters, setFilters] = useState({
    type: '',
    category: '',
    startDate: '',
    endDate: '',
    page: 1,
    limit: 50,
    sortBy: 'date',
    sortOrder: 'desc'
  });
  const [isExporting, setIsExporting] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    count: 0
  });

  // Form state
  const [formData, setFormData] = useState({
    type: 'expense',
    category: '',
    subcategory: '',
    title: '',
    description: '',
    amount: '',
    currency: 'INR',
    date: new Date().toISOString().split('T')[0],
    source: '',
    account: '',
    tags: '',
    isRecurring: false,
    recurringFrequency: '',
    recurringEndDate: '',
    receipt: '',
    attachments: '',
    status: 'confirmed',
    taxDeductible: false,
    taxCategory: '',
    budgetCategory: '',
    notes: '',
    // Add connectivity fields to form data
    paidFrom: 'none',
    cashLink: {
      type: 'physical-cash',
      location: '',
      walletProvider: '',
      date: new Date().toISOString().slice(0, 10),
      currency: 'INR',
    },
    cardLink: { cardId: '', date: new Date().toISOString().slice(0, 10), currency: 'INR' },
    bankLink: { accountId: '', date: new Date().toISOString().slice(0, 10), currency: 'INR' }
  });

  // Seed list, only ever used to populate the Category collection the very first
  // time a tenant opens this screen (see fetchUsedCategories). After that, categories
  // live entirely in the backend, so deleting one via Manage Categories actually
  // removes it for good instead of it reappearing.
  const defaultCategories = {
    income: ['consultation-fees', 'salary', 'business', 'investment', 'rental', 'freelance', 'gift', 'refund', 'other-income'],
    expense: ['food', 'transport', 'shopping', 'entertainment', 'utilities', 'healthcare', 'education', 'rent', 'insurance', 'loan-payment', 'tax', 'other-expense']
  };

  // Category options for the Add Record / filter dropdowns come entirely from
  // usedCategories (saved Category docs + any legacy free-text categories still
  // referenced on old records), so a deleted category actually disappears.
  const categories = useMemo(() => ({
    income: [...(usedCategories.income || [])],
    expense: [...(usedCategories.expense || [])],
  }), [usedCategories]);

  const paymentMethods = ['cash', 'card', 'bank-transfer', 'upi', 'wallet', 'other'];
  const recurringFrequencies = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'];
  const currencies = ['INR', 'USD', 'EUR', 'GBP', 'JPY', 'AUD', 'CAD', 'CHF', 'CNY'];

  // Sync paidFrom with formData.paidFrom
  useEffect(() => {
    setPaidFrom(formData.paidFrom);
    if (formData.cashLink) setCashLink(formData.cashLink);
    if (formData.cardLink) setCardLink(formData.cardLink);
    if (formData.bankLink) setBankLink(formData.bankLink);
  }, [formData.paidFrom, formData.cashLink, formData.cardLink, formData.bankLink]);

  useEffect(() => {
    fetchRecords();
    fetchSummary();
  }, [filters]);

  useEffect(() => {
    fetchUsedCategories();
  }, []);

  useEffect(() => {
    // Fetch cards and bank accounts for connectivity
    const fetchConnectivityData = async () => {
      try {
        console.log('Fetching connectivity data...');
        const [cardsResponse, bankResponse] = await Promise.all([
          axios.get(`${END_POINT}/cards`, {headers: {Authorization: getJwtToken()}}),
          axios.get(`${END_POINT}/bank`, {headers: {Authorization: getJwtToken()}})
        ]);
        console.log('Cards response:', cardsResponse.data);
        console.log('Bank response:', bankResponse.data);
        console.log('Bank response length:', bankResponse.data?.length);
        
        setCards(cardsResponse.data || []);
        setBankAccounts(bankResponse.data || []);
        
        // Check if bank data is actually being set
        if (bankResponse.data && bankResponse.data.length > 0) {
          console.log('Bank accounts loaded:', bankResponse.data);
        } else {
          console.log('No bank accounts found or empty response');
        }
      } catch (error) {
        console.error('Error fetching connectivity data:', error);
      }
    };
    
    fetchConnectivityData();
  }, []);

  // const fetchRecords = async () => {
  //   try {
  //     setLoading(true);
  //     const response = await incomeExpenseaxios.getRecords(filters);
  //     setRecords(response.data.data);
  //     // setPagination(response.data.pagination);
  //   } catch (error) {
  //     console.error('Error fetching records:', error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  const fetchRecords = async () => {
  try {
    setLoading(true);

    const cleanFilters = {};

    if (filters.type) cleanFilters.type = filters.type;
    if (filters.category) cleanFilters.category = filters.category;
    if (filters.startDate) cleanFilters.startDate = filters.startDate;
    if (filters.endDate) cleanFilters.endDate = filters.endDate;

    const response = await incomeExpenseaxios.getRecords(cleanFilters);

    setRecords(response.data.data || []);
    setPagination(response.data.pagination || {});
  } catch (error) {
    console.error('Error fetching records:', error);
  } finally {
    setLoading(false);
  }
};

  // One-time, per-browser flag so the default category list is only ever pushed
  // into the backend once. Without this, deleting every category would just have
  // them come back the next time the screen loads.
  const CATEGORIES_SEEDED_KEY = 'incomeExpenseCategoriesSeeded';

  // Fetches saved categories (for both the dropdowns and the Manage Categories
  // modal), plus ALL records (ignoring current filters) to also pick up categories
  // that were typed as one-off free text before ever being saved as a managed
  // category. On a tenant's very first visit (no categories saved yet at all) it
  // seeds the built-in default list into the backend so it shows up as normal,
  // deletable categories from then on.
  const fetchUsedCategories = async () => {
    try {
      let categoriesRes = await incomeExpenseaxios.getCategories();
      let savedCategories = categoriesRes.data.data || [];

      const alreadySeeded = localStorage.getItem(CATEGORIES_SEEDED_KEY) === '1';
      if (!alreadySeeded && savedCategories.length === 0) {
        try {
          await Promise.all([
            ...defaultCategories.income.map((name) => incomeExpenseaxios.createCategory({ name, type: 'income' })),
            ...defaultCategories.expense.map((name) => incomeExpenseaxios.createCategory({ name, type: 'expense' })),
          ]);
        } finally {
          localStorage.setItem(CATEGORIES_SEEDED_KEY, '1');
        }
        categoriesRes = await incomeExpenseaxios.getCategories();
        savedCategories = categoriesRes.data.data || [];
      }

      const income = savedCategories.filter((c) => c.type === 'income');
      const expense = savedCategories.filter((c) => c.type === 'expense');
      setCustomCategories({ income, expense });

      const recordsRes = await incomeExpenseaxios.getRecords({});
      const all = recordsRes.data.data || [];
      const legacyIncome = all.filter((r) => r.type === 'income').map((r) => r.category).filter(Boolean);
      const legacyExpense = all.filter((r) => r.type === 'expense').map((r) => r.category).filter(Boolean);

      setUsedCategories({
        income: [...new Set([...income.map((c) => c.name), ...legacyIncome])],
        expense: [...new Set([...expense.map((c) => c.name), ...legacyExpense])],
      });
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleAddCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    try {
      setCategorySaving(true);
      await incomeExpenseaxios.createCategory({ name, type: categoryManagerType });
      setNewCategoryName('');
      await fetchUsedCategories();
    } catch (error) {
      console.error('Error adding category:', error);
    } finally {
      setCategorySaving(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Remove this category?')) return;
    try {
      await incomeExpenseaxios.deleteCategory(id);
      await fetchUsedCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };


  // const fetchSummary = async () => {
  //   try {
  //     console.log('Fetching summary with filters:', filters);
  //     const response = await incomeExpenseaxios.getSummary({
  //       startDate: filters.startDate,
  //       endDate: filters.endDate
  //     });
  //     console.log('Summary response:', response.data);
  //     setSummary(response.data);
  //   } catch (error) {
  //     console.error('Error fetching summary:', error);
  //     // Set summary to null to trigger fallback calculation
  //     setSummary(null);
  //   }
  // };
  const fetchSummary = async () => {
  try {
    const payload = {};
    if (filters.startDate) payload.startDate = filters.startDate;
    if (filters.endDate) payload.endDate = filters.endDate;

    const response = await incomeExpenseaxios.getSummary(payload);
    setSummary(response.data);
  } catch {
    setSummary(null);
  }
};


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = {
        ...formData,
        amount: parseFloat(formData.amount),
        tags: formData.tags ? formData.tags.split(',')?.map(tag => tag.trim()) : [],
        attachments: formData.attachments ? formData.attachments.split(',')?.map(att => att.trim()) : [],
        date: new Date(formData.date),
        recurringEndDate: formData.recurringEndDate ? new Date(formData.recurringEndDate) : undefined,
        // Add default paymentMethod for expenses to satisfy backend validation
        paymentMethod: formData.type === 'expense' ? 'other' : undefined,
        // Convert empty strings to undefined for enum fields
        recurringFrequency: formData.recurringFrequency || undefined,
        source: formData.source || undefined,
        taxCategory: formData.taxCategory || undefined,
        receipt: formData.receipt || undefined,
        attachments: formData.attachments ? formData.attachments.split(',')?.map(att => att.trim()) : [],
        // Only include recurring fields if isRecurring is true
        ...(formData.isRecurring ? {
          recurringFrequency: formData.recurringFrequency || undefined,
          recurringEndDate: formData.recurringEndDate ? new Date(formData.recurringEndDate) : undefined
        } : {
          recurringFrequency: undefined,
          recurringEndDate: undefined
        }),
        // Only include tax fields if taxDeductible is true
        ...(formData.taxDeductible ? {
          taxCategory: formData.taxCategory || undefined
        } : {
          taxCategory: undefined
        })
      };

      // Remove empty strings from optional fields
      Object.keys(dataToSubmit).forEach(key => {
        if (dataToSubmit[key] === '') {
          dataToSubmit[key] = undefined;
        }
      });

      // Create income/expense record
      let savedRecord;
      if (editingRecord) {
        savedRecord = await incomeExpenseaxios.updateRecord(editingRecord._id, dataToSubmit);
      } else {
        savedRecord = await incomeExpenseaxios.createRecord(dataToSubmit);
      }

      // Add connectivity logic - create records in cash/cards/bank if paidFrom is selected
      if (paidFrom !== 'none' && formData.status === 'confirmed') {
        const amount = parseFloat(formData.amount) || 0;
        const payDate = formData.date || new Date().toISOString().slice(0, 10);
        
        try {
          if (paidFrom === 'cash') {
            const payload = {
              type: cashLink.type,
              name: `${formData.type === 'income' ? 'Income' : 'Expense'} - ${formData.title}`,
              currency: cashLink.currency || formData.currency || 'INR',
              amount: Math.abs(amount), // Cash model requires positive amount
              date: cashLink.date || payDate,
              location: cashLink.type === 'physical-cash' ? (cashLink.location || 'Wallet') : undefined,
              walletProvider: cashLink.type === 'digital-wallet' ? (cashLink.walletProvider || 'Wallet') : undefined,
              walletType: 'prepaid',
              description: `${formData.category} - ${formData.description || formData.title}`,
              notes: `Auto-linked from Income/Expenses (${formData.type})`,
            };
            await axios.post(`${END_POINT}/cash`, payload, {headers: {Authorization: getJwtToken()}});
          } else if (paidFrom === 'card' && cardLink.cardId) {
            const payload = {
              cardId: cardLink.cardId,
              type: formData.type === 'income' ? 'credit' : 'payment',
              amount,
              merchant: formData.title,
              category: formData.category,
              description: `${formData.type === 'income' ? 'Income' : 'Expense'} - ${formData.title}`,
              date: cardLink.date || payDate,
              currency: cardLink.currency || formData.currency || 'INR',
            };
            await axios.post(`${END_POINT}/transactions`, payload, {headers: {Authorization: getJwtToken()}});
          } else if (paidFrom === 'bank' && bankLink.accountId) {
            // Map income/expense categories to bank transaction categories
            const mapToBankCategory = (category) => {
              const bankCategories = ['food', 'shopping', 'transport', 'entertainment', 'utilities', 'healthcare', 'education', 'salary', 'rent', 'other'];
              return bankCategories.includes(category) ? category : 'other';
            };
            
            const payload = {
              accountId: bankLink.accountId,
              type: formData.type === 'income' ? 'deposit' : 'withdrawal',
              amount,
              merchant: formData.title,
              category: mapToBankCategory(formData.category),
              description: `${formData.type === 'income' ? 'Income' : 'Expense'} - ${formData.title}`,
              date: bankLink.date || payDate,
              currency: bankLink.currency || formData.currency || 'INR',
            };
            await axios.post('/bank-transactions', payload, {headers: {Authorization: getJwtToken()}});
          }
        } catch (connectivityError) {
          console.error('Error creating connectivity record:', connectivityError);
          // Don't fail the main operation if connectivity fails
          // Just log the error and continue
        }
      }

      // Only reset after all operations are complete
      setShowModal(false);
      setEditingRecord(null);
      resetForm();
      fetchRecords();
      fetchSummary();
      fetchUsedCategories();
    } catch (error) {
      console.error('Error saving record:', error);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      ...record,
      tags: record.tags ? record.tags.join(', ') : '',
      attachments: record.attachments ? record.attachments.join(', ') : '',
      date: new Date(record.date).toISOString().split('T')[0],
      recurringEndDate: record.recurringEndDate ? new Date(record.recurringEndDate).toISOString().split('T')[0] : '',
      // Ensure connectivity objects exist
      paidFrom: record.paidFrom || 'none',
      cashLink: record.cashLink || {
        type: 'physical-cash',
        location: '',
        walletProvider: '',
        date: new Date().toISOString().slice(0, 10),
        currency: 'INR',
      },
      cardLink: record.cardLink || { cardId: '', date: new Date().toISOString().slice(0, 10), currency: 'INR' },
      bankLink: record.bankLink || { accountId: '', date: new Date().toISOString().slice(0, 10), currency: 'INR' }
    });
    setIsCustomCategory(Boolean(record.category) && !categories[record.type]?.includes(record.category));

    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      try {
        await incomeExpenseaxios.deleteRecord(id);
        fetchRecords();
        fetchSummary();
      } catch (error) {
        console.error('Error deleting record:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'expense',
      category: '',
      subcategory: '',
      title: '',
      description: '',
      amount: '',
      currency: 'INR',
      date: new Date().toISOString().slice(0, 10),
      source: '',
      account: '',
      tags: '',
      isRecurring: false,
      recurringFrequency: '',
      recurringEndDate: '',
      receipt: '',
      attachments: '',
      status: 'confirmed',
      taxDeductible: false,
      taxCategory: '',
      budgetCategory: '',
      notes: '',
      // Reset connectivity fields
      paidFrom: 'none',
      cashLink: {
        type: 'physical-cash',
        location: '',
        walletProvider: '',
        date: new Date().toISOString().slice(0, 10),
        currency: 'INR',
      },
      cardLink: { cardId: '', date: new Date().toISOString().slice(0, 10), currency: 'INR' },
      bankLink: { accountId: '', date: new Date().toISOString().slice(0, 10), currency: 'INR' }
    });
    setIsCustomCategory(false);

    // Reset separate states for sync
    setPaidFrom('none');
    setCashLink({
      type: 'physical-cash',
      location: '',
      walletProvider: '',
      date: new Date().toISOString().slice(0, 10),
      currency: 'INR',
    });
    setCardLink({ cardId: '', date: new Date().toISOString().slice(0, 10), currency: 'INR' });
    setBankLink({ accountId: '', date: new Date().toISOString().slice(0, 10), currency: 'INR' });
  };

  // const handleFilterChange = (key, value) => {
  //   setFilters(prev => ({ ...prev, [key]: value }));
  // };
  const handleFilterChange = (key, value) => {
  setFilters(prev => ({
    ...prev,
    [key]: value,
    ...(key === 'type' ? { category: '' } : {})
  }));
};

  const openModal = () => {
    resetForm();
    setEditingRecord(null);
    setShowModal(true);
  };

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getSummaryStats = () => {
    // Debug: log the summary data
    console.log('Summary data:', summary);
    
    if (!summary || !summary.summary || summary.summary.length === 0) {
      // Fallback: calculate from records if axios doesn't work
      const income = records?.filter(r => r.type === 'income').reduce((sum, r) => sum + r.amount, 0);
      const expense = records?.filter(r => r.type === 'expense').reduce((sum, r) => sum + r.amount, 0);
      console.log('Fallback calculation - Income:', income, 'Expense:', expense);
      return { income, expense, balance: income - expense };
    }
    
    const income = summary.summary.find(s => s._id === 'income')?.totalAmount || 0;
    const expense = summary.summary.find(s => s._id === 'expense')?.totalAmount || 0;
    console.log('axios calculation - Income:', income, 'Expense:', expense);
    
    return { income, expense, balance: income - expense };
  };

  const handleFormChange = (e) => {
  const { name, value } = e.target;
  setFormData((prev) => ({
    ...prev,
    [name]: value,
  }));
};

  const handleCategorySelectChange = (e) => {
    const { value } = e.target;
    if (value === '__custom__') {
      setIsCustomCategory(true);
      setFormData((prev) => ({ ...prev, category: '' }));
    } else {
      setFormData((prev) => ({ ...prev, category: value }));
    }
  };

  const stats = getSummaryStats();

  // Exports every record matching the current filters (all pages).
  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const cleanFilters = {};
      if (filters.type) cleanFilters.type = filters.type;
      if (filters.category) cleanFilters.category = filters.category;
      if (filters.startDate) cleanFilters.startDate = filters.startDate;
      if (filters.endDate) cleanFilters.endDate = filters.endDate;
      const all = await fetchAllPages(async (page, limit) => {
        const response = await incomeExpenseaxios.getRecords({ ...cleanFilters, page, limit });
        return response?.data?.data || [];
      });
      exportToExcel(all, 'Income_Expenses', [
        { key: 'date', label: 'Date', format: formatExportDate },
        { key: 'type', label: 'Type' },
        { key: 'category', label: 'Category' },
        { key: 'title', label: 'Title' },
        { key: 'amount', label: 'Amount' },
        { key: 'currency', label: 'Currency' },
        { key: 'paymentMethod', label: 'Payment Method' },
        { key: 'tags', label: 'Tags', format: (tags) => (Array.isArray(tags) ? tags.join(', ') : tags || '') },
        { key: 'description', label: 'Description' },
      ], 'Income & Expenses');
    } catch (error) {
      console.error('Error exporting records:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
   <div className="min-h-screen w-full p-4 md:p-6">

  {/* Page Header */}
  <div className="mb-6">
    <h1 className="text-2xl font-semibold text-gray-900">Income & Expenses</h1>
    <p className="text-sm text-gray-500">
      Manage your daily income and expense records
    </p>
  </div>

  {/* Summary Cards */}
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
    <div className="bg-white rounded-xl border shadow-sm p-5 flex justify-between items-center">
      <div>
        <p className="text-sm text-gray-500">Total Income</p>
        <p className="text-xl font-semibold text-green-600">
          {formatCurrency(stats?.income)}
        </p>
      </div>
      <FiTrendingUp className="text-green-500 text-2xl" />
    </div>

    <div className="bg-white rounded-xl border shadow-sm p-5 flex justify-between items-center">
      <div>
        <p className="text-sm text-gray-500">Total Expenses</p>
        <p className="text-xl font-semibold text-red-600">
          {formatCurrency(stats?.expense)}
        </p>
      </div>
      <FiTrendingDown className="text-red-500 text-2xl" />
    </div>

    <div className="bg-white rounded-xl border shadow-sm p-5 flex justify-between items-center">
      <div>
        <p className="text-sm text-gray-500">Balance</p>
        <p
          className={`text-xl font-semibold ${
            stats.balance >= 0 ? 'text-green-600' : 'text-red-600'
          }`}
        >
          {formatCurrency(stats.balance)}
        </p>
      </div>
      <FiDollarSign className="text-gray-500 text-2xl" />
    </div>
  </div>

  {/* Filters */}
  <div className="bg-white rounded-xl border shadow-sm p-5 mb-6">
    <div className="flex items-center gap-2 mb-4">
      <FiFilter className="text-gray-500" />
      <h3 className="font-medium text-gray-800">Filters</h3>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
      <select
        value={filters.type}
        onChange={(e) => handleFilterChange('type', e.target.value)}
        className="border rounded-md px-3 py-2 text-sm"
      >
        <option value="">All Types</option>
        <option value="income">Income</option>
        <option value="expense">Expense</option>
      </select>

      <select
        value={filters.category}
        onChange={(e) => handleFilterChange('category', e.target.value)}
        disabled={!filters.type}
        className="border rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
      >
        <option value="">All Categories</option>
        {filters.type &&
          categories[filters.type]?.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
      </select>

      <input
        type="date"
        value={filters.startDate}
        onChange={(e) => handleFilterChange('startDate', e.target.value)}
        className="border rounded-md px-3 py-2 text-sm"
      />

      <input
        type="date"
        value={filters.endDate}
        onChange={(e) => handleFilterChange('endDate', e.target.value)}
        className="border rounded-md px-3 py-2 text-sm"
      />

      <button
        type="button"
        onClick={() => {
          setCategoryManagerType(filters.type || 'expense');
          setNewCategoryName('');
          setShowCategoryModal(true);
        }}
        className="bg-white border text-gray-700 rounded-md px-4 py-2 flex items-center gap-2 justify-center hover:bg-gray-50"
      >
        <FiTag /> Manage Categories
      </button>

      <button
        onClick={openModal}
        className="bg-blue-600 text-white rounded-md px-4 py-2 flex items-center gap-2 justify-center"
      >
        <FiPlus /> Add Record
      </button>

      <button
        type="button"
        onClick={handleExportExcel}
        disabled={isExporting}
        className="bg-white border text-gray-700 rounded-md px-4 py-2 flex items-center gap-2 justify-center hover:bg-gray-50 disabled:opacity-50"
      >
        <FiDownload /> {isExporting ? 'Exporting...' : 'Export to Excel'}
      </button>
    </div>
  </div>

  {/* Table */}
  <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
    {loading ? (
      <div className="p-6 text-center text-gray-500">Loading records...</div>
    ) : records.length === 0 ? (
      <div className="p-6 text-center text-gray-500">No records found</div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Tags</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>

          <tbody>
            {records.map((record) => (
              <tr key={record._id} className="border-t">
                <td className="px-4 py-3">
                  {new Date(record.date).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-center">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      record.type === 'income'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {record.type}
                  </span>
                </td>
                <td className="px-4 py-3">{record.category}</td>
                <td className="px-4 py-3">{record.title}</td>
                <td
                  className={`px-4 py-3 font-medium ${
                    record.type === 'income'
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}
                >
                  {formatCurrency(record.amount, record.currency)}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {record.tags?.map((tag, i) => (
                      <span
                        key={i}
                        className="bg-gray-100 text-xs px-2 py-1 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(record)}
                      className="text-blue-600 hover:underline"
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      onClick={() => handleDelete(record._id)}
                      className="text-red-600 hover:underline"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>

  {/* Pagination */}
  {pagination.total > 1 && (
    <div className="flex justify-between items-center mt-6">
      <button
        disabled={pagination.current === 1}
        onClick={() =>
          handleFilterChange('page', pagination.current - 1)
        }
        className="px-4 py-2 border rounded-md disabled:opacity-50"
      >
        Previous
      </button>

      <span className="text-sm text-gray-600">
        Page {pagination.current} of {pagination.total}
      </span>

      <button
        disabled={pagination.current === pagination.total}
        onClick={() =>
          handleFilterChange('page', pagination.current + 1)
        }
        className="px-4 py-2 border rounded-md disabled:opacity-50"
      >
        Next
      </button>
    </div>
  )}

  {/* Modal */}
{showModal && (
  <ModalPortal isOpen={showModal} onClose={() => setShowModal(false)}>
  <div className={`fixed inset-0 ${MODAL_Z} flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4`}>
    <div role="dialog" aria-modal="true" className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">

      {/* Modal Header */}
      <div className="flex justify-between items-center px-6 py-4 border-b">
        <h2 className="text-lg font-semibold text-gray-800">
          {editingRecord ? 'Edit Record' : 'Add Record'}
        </h2>
        <button
          onClick={() => setShowModal(false)}
          className="text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
      </div>

      {/* Modal Body */}
      <form onSubmit={handleSubmit} className="px-6 py-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          <div>
            <label className="text-sm font-medium text-gray-700">Type</label>
            <select
              name="type"
              value={formData.type}
              onChange={handleFormChange}
              className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
              required
            >
              <option value="expense">Expense</option>
              <option value="income">Income</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Category</label>
            {isCustomCategory ? (
              <div className="mt-1 flex gap-2">
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleFormChange}
                  placeholder="Enter custom category"
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  required
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => {
                    setIsCustomCategory(false);
                    setFormData((prev) => ({ ...prev, category: '' }));
                  }}
                  className="text-xs text-blue-600 hover:underline whitespace-nowrap"
                >
                  Choose from list
                </button>
              </div>
            ) : (
              <select
                name="category"
                value={formData.category}
                onChange={handleCategorySelectChange}
                className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                required
              >
                <option value="">Select Category</option>
                {categories[formData.type]?.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
                <option value="__custom__">+ Add new category</option>
              </select>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Title</label>
            <input
              name="title"
              value={formData.title}
              onChange={handleFormChange}
              className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Amount</label>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleFormChange}
              className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Currency</label>
            <select
              name="currency"
              value={formData.currency}
              onChange={handleFormChange}
              className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
            >
              <option value="INR">INR</option>
              <option value="USD">USD</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Date</label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleFormChange}
              className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-sm font-medium text-gray-700">
              Tags (comma separated)
            </label>
            <input
              name="tags"
              value={formData.tags}
              onChange={handleFormChange}
              className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
              placeholder="food, travel, office"
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleFormChange}
              rows={3}
              className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
            />
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={() => setShowModal(false)}
            className="px-4 py-2 border rounded-md text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm"
          >
            {editingRecord ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  </div></ModalPortal>
)}

{showCategoryModal && (
  <ModalPortal isOpen={showCategoryModal} onClose={() => setShowCategoryModal(false)}>
  <div className={`fixed inset-0 ${MODAL_Z} flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4`}>
    <div role="dialog" aria-modal="true" className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white shadow-2xl">
      <div className="flex justify-between items-center px-6 py-4 border-b">
        <h2 className="text-lg font-semibold text-gray-800">Manage Categories</h2>
        <button
          onClick={() => setShowCategoryModal(false)}
          className="text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
      </div>

      <div className="px-6 py-4">
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={() => setCategoryManagerType('expense')}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium ${categoryManagerType === 'expense' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            Expense
          </button>
          <button
            type="button"
            onClick={() => setCategoryManagerType('income')}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium ${categoryManagerType === 'income' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            Income
          </button>
        </div>

        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddCategory();
              }
            }}
            placeholder={`New ${categoryManagerType} category`}
            className="flex-1 border rounded-md px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={handleAddCategory}
            disabled={!newCategoryName.trim() || categorySaving}
            className="bg-blue-600 text-white rounded-md px-4 py-2 text-sm disabled:opacity-50"
          >
            Add
          </button>
        </div>

        <div className="max-h-72 overflow-y-auto space-y-1">
          {customCategories[categoryManagerType].length === 0 ? (
            <p className="text-sm text-gray-400 px-3 py-2">No categories yet</p>
          ) : (
            customCategories[categoryManagerType].map((cat) => (
              <div key={cat._id} className="flex items-center justify-between px-3 py-2 rounded-md border text-sm text-gray-700">
                <span>{cat.name}</span>
                <button
                  type="button"
                  onClick={() => handleDeleteCategory(cat._id)}
                  title="Delete category"
                  className="text-red-500 hover:text-red-700"
                >
                  <FiTrash2 />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  </div></ModalPortal>
)}

</div>

  );
};

export default IncomeExpenses;